/**
 * ValuationDisclaimer Component
 *
 * Swiss financial regulation disclaimer for all valuation displays.
 * Two variants: full (info box) and compact (single line).
 */

import React from 'react';
import { View, Text } from 'react-native';
import { Info, AlertTriangle } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';

// ============================================================================
// Types
// ============================================================================

interface ValuationDisclaimerProps {
  /** 'full' = info box with detailed text; 'compact' = single line */
  variant: 'full' | 'compact';
  /** Optional methodology version to display */
  methodologyVersion?: string;
  /** Optional computed-at timestamp */
  computedAt?: string;
}

// ============================================================================
// Component
// ============================================================================

export function ValuationDisclaimer({
  variant,
  methodologyVersion,
  computedAt,
}: ValuationDisclaimerProps) {
  const colors = useThemeColors();

  // ── Compact Variant ──────────────────────────────────────────────
  if (variant === 'compact') {
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 4 }}>
        <AlertTriangle size={12} color={colors.text.tertiary} />
        <Text
          className="text-xs"
          style={{
            fontStyle: 'italic',
            color: colors.text.tertiary,
            flex: 1,
          }}
        >
          Indicative estimate only — not financial advice.
        </Text>
      </View>
    );
  }

  // ── Full Variant ─────────────────────────────────────────────────
  const formattedDate = computedAt
    ? new Date(computedAt).toLocaleDateString('en-CH', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;

  return (
    <View
      style={{
        backgroundColor: colors.primary[50],
        borderWidth: 1,
        borderColor: colors.primary[100],
        borderRadius: 16,
        padding: 16,
        flexDirection: 'row',
        gap: 12,
      }}
    >
      <Info size={20} color={colors.primary[600]} style={{ marginTop: 2 }} />
      <View style={{ flex: 1 }}>
        <Text
          className="text-sm font-semibold"
          style={{
            color: colors.primary[700],
            marginBottom: 6,
          }}
        >
          Indicative Valuation Estimate
        </Text>
        <Text
          className="text-xs"
          style={{
            color: colors.primary[600],
            lineHeight: 18,
          }}
        >
          This valuation is a model-based indicative estimate and does not constitute financial advice,
          an investment recommendation, or a binding offer. Inverio is not regulated by FINMA and
          does not provide regulated financial services. Always consult a qualified financial advisor
          before making investment decisions.
        </Text>

        {/* Metadata row */}
        {(methodologyVersion || formattedDate) && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              marginTop: 8,
              paddingTop: 8,
              borderTopWidth: 1,
              borderTopColor: colors.primary[100],
            }}
          >
            {methodologyVersion && (
              <Text
                className="text-xs font-medium"
                style={{
                  color: colors.primary[500],
                }}
              >
                Methodology v{methodologyVersion}
              </Text>
            )}
            {formattedDate && (
              <Text
                className="text-xs font-medium"
                style={{
                  color: colors.primary[500],
                }}
              >
                Computed {formattedDate}
              </Text>
            )}
          </View>
        )}
      </View>
    </View>
  );
}
